import React, { Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";

import ProtectedRoute from "./components/ProtectedRoute";
import Layout from "./components/Layout";
import "./App.css";

import Home from "./pages/Home";
import About from "./pages/About";
import AuthCallback from "./pages/AuthCallback";
import Vote from "./pages/Vote";
import Rankings from "./pages/Rankings";
import Profile from "./pages/Profile";
import UserProfile from "./pages/UserProfile";
import OnboardingPage from "./pages/OnboardingPage";
import NotFound from "./pages/NotFound";
import { Analytics } from "@vercel/analytics/react";

import { useAuth } from "./hooks/useAuth";

/* ------------------------------------------------------------------ */

const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen flex-col">
    <p>Loading…</p>
  </div>
);

function App() {
  const {
    session,
    sessionStatus, // 'signed_out' | 'signed_in_no_profile' | 'signed_in_with_profile'
    isLoading,
    profile,
    refreshProfile,
    signOut,
    authError,
  } = useAuth();

  /* derived flags for convenience */
  const isSignedIn = sessionStatus !== "signed_out";
  const hasProfile = sessionStatus === "signed_in_with_profile";

  /* -------------------------- error --------------------- */
  if (authError) {
    return (
      <div className="flex items-center justify-center min-h-screen flex-col">
        <p className="mb-2 text-red-600">Error: {authError.message}</p>
        {isSignedIn && (
          <button
            onClick={signOut}
            className="px-4 py-2 rounded bg-red-500 text-white font-medium"
          >
            Sign Out
          </button>
        )}
      </div>
    );
  }

  if (isLoading) {
    return <LoadingFallback />;
  }

  /* ------------------------------- routes ------------------------- */
  return (
    <div className="app-container">
      <Router>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            {/* --- Home ------------------------------------------------ */}
            <Route
              path="/"
              element={
                isSignedIn && hasProfile ? (
                  <Navigate to="/vote" replace />
                ) : isSignedIn && !hasProfile ? (
                  <Navigate to="/onboarding" replace />
                ) : (
                  <Layout>
                    <Home />
                  </Layout>
                )
              }
            />

            {/* --- About ---------------------------------------------- */}
            <Route
              path="/about"
              element={
                <Layout>
                  <About />
                </Layout>
              }
            />

            {/* --- Supabase OAuth callback ---------------------------- */}
            <Route path="/auth/callback" element={<AuthCallback />} />

            {/* --- Onboarding ----------------------------------------- */}
            <Route
              path="/onboarding"
              element={
                !isSignedIn ? (
                  <Navigate to="/" replace />
                ) : hasProfile ? (
                  <Navigate to="/vote" replace />
                ) : (
                  <Layout>
                    <OnboardingPage
                      userProfile={{
                        id: session!.user.id,
                        email: session!.user.email,
                      }}
                      refreshProfile={refreshProfile}
                    />
                  </Layout>
                )
              }
            />

            {/* --- Vote ---------------------------------------------- */}
            <Route
              path="/vote"
              element={
                <ProtectedRoute redirectTo="/">
                  {!hasProfile ? (
                    <Navigate to="/onboarding" replace />
                  ) : (
                    <Layout>
                      <Vote />
                    </Layout>
                  )}
                </ProtectedRoute>
              }
            />

            {/* --- Rankings ------------------------------------------ */}
            <Route
              path="/rankings"
              element={
                <ProtectedRoute redirectTo="/">
                  {!hasProfile ? (
                    <Navigate to="/onboarding" replace />
                  ) : (
                    <Layout>
                      <Rankings />
                    </Layout>
                  )}
                </ProtectedRoute>
              }
            />

            {/* --- Current user profile ------------------------------ */}
            <Route
              path="/profile"
              element={
                <ProtectedRoute redirectTo="/">
                  {!hasProfile ? (
                    <Navigate to="/onboarding" replace />
                  ) : (
                    <Layout>
                      <Profile />
                    </Layout>
                  )}
                </ProtectedRoute>
              }
            />

            {/* --- Other users' profiles ----------------------------- */}
            <Route
              path="/users/:userId"
              element={
                <ProtectedRoute redirectTo="/">
                  {!hasProfile ? (
                    <Navigate to="/onboarding" replace />
                  ) : (
                    <Layout>
                      <UserProfile />
                    </Layout>
                  )}
                </ProtectedRoute>
              }
            />

            {/* --- 404 fallback -------------------------------------- */}
            <Route
              path="*"
              element={
                <Layout>
                  <NotFound />
                </Layout>
              }
            />
          </Routes>
        </Suspense>
      </Router>
      <Analytics />
    </div>
  );
}

export default App;
